import { useState } from 'react';
import omit from 'lodash/omit';
import { useToastContext } from '@/contexts/toastContext';
import useApiSubmit from '@/hooks/useApiSubmit';
import useProfile from '@/hooks/useProfileV2';
import interviewsServices, {
  AddInterviewNote,
  DeleteInterviewNote,
  UpdateInterviewNote,
} from '@/services/interviews';
import { AvailableRoles } from '@/types';
import { InterviewNoteType, InterviewType } from '@/types/api';
import InterviewNotesManagerComponent, { CustomNoteType } from './InterviewNotesManager.component';

type InterviewNotesManagerProps = {
  interview: InterviewType;
  canAddNewNote?: boolean;
  onRefetchInterview: () => void;
};

export default function InterviewNotesManager({
  interview,
  canAddNewNote = true,
  onRefetchInterview,
}: InterviewNotesManagerProps): JSX.Element {
  const { profile } = useProfile();
  const toast = useToastContext();

  const addInterviewNoteMutation = useApiSubmit<any, AddInterviewNote>(
    interviewsServices.addNoteInterview
  );

  const deleteInterviewNoteMutation = useApiSubmit<any, DeleteInterviewNote>(
    interviewsServices.deleteNoteInterview
  );

  const updateInterviewNoteMutation = useApiSubmit<any, UpdateInterviewNote>(
    interviewsServices.updateNoteInterview
  );

  const [notes, setNotes] = useState<CustomNoteType[]>(() => {
    if (interview.notes.length) {
      return interview.notes.map((note) => ({
        ...note,
        isEditing: false,
      }));
    }

    return [];
  });

  const onAddNewNote = (): void => {
    setNotes((prev) =>
      prev.concat({
        note: '',
        user: profile,
        isEditing: true,
      })
    );
  };

  const updateNoteAfterSubmit = (index: number, persistedNewNote: InterviewNoteType): void => {
    setNotes((prev) =>
      prev.map((note, noteIndex) => {
        if (index !== noteIndex) {
          return note;
        }

        return {
          ...omit(persistedNewNote, ['interview']),
          isEditing: false,
        };
      })
    );
  };

  const onCancelAddNewInterviewNote = (interviewNoteIndex: number): void => {
    setNotes((prev) =>
      prev.filter((_, index) => {
        return interviewNoteIndex !== index;
      })
    );
  };

  const onAddNewInterviewNote = async (
    index: number,
    newNote: CustomNoteType
  ): Promise<boolean> => {
    const { id } = newNote;
    const isAlreadyCreated = !!id;
    const actionsVerb = isAlreadyCreated ? 'updated' : 'added';

    const methodToCall = isAlreadyCreated ? updateInterviewNoteMutation : addInterviewNoteMutation;

    try {
      const noteWithOmittedFields = omit(newNote, ['isEditing', !isAlreadyCreated ? 'id' : '']);

      const persistedNote = await methodToCall.mutateAsync({
        interviewId: interview.id,
        interviewNoteId: id,
        ...noteWithOmittedFields,
      });

      updateNoteAfterSubmit(index, persistedNote);
      onRefetchInterview();
      toast.showSuccessFeedback(`The note has been ${actionsVerb} to the interview.`);
    } catch {
      const actionContinuousVerb = actionsVerb.replace('ed', 'ing');

      toast.showErrorFeedback(
        `There was a problem to ${actionContinuousVerb} the note to the interview.`
      );
    }

    return methodToCall.isSuccess;
  };

  const onDeleteInterviewNote = async (interviewNoteId: string): Promise<boolean> => {
    try {
      await deleteInterviewNoteMutation.mutateAsync({
        interviewId: interview.id,
        interviewNoteId,
      });

      setNotes((prev) =>
        prev.filter((note) => {
          return interviewNoteId !== note.id;
        })
      );
      onRefetchInterview();
      toast.showSuccessFeedback('The note has been deleted of the interview.');
    } catch {
      toast.showErrorFeedback('There was a problem to delete the note of the interview.');
    }

    return deleteInterviewNoteMutation.isSuccess;
  };

  const isActionLoading =
    addInterviewNoteMutation.isLoading ||
    updateInterviewNoteMutation.isLoading ||
    deleteInterviewNoteMutation.isLoading;

  return (
    <InterviewNotesManagerComponent
      interviewId={interview.id}
      canAddNewNote={canAddNewNote}
      notes={notes}
      isActionLoading={isActionLoading}
      onInterviewUpdate={onRefetchInterview}
      onAddNewInterviewNote={onAddNewInterviewNote}
      onAddNewNote={onAddNewNote}
      onDeleteInterviewNote={onDeleteInterviewNote}
      onCancelAddNewInterviewNote={onCancelAddNewInterviewNote}
      interviewRecordingUrl={interview.recordingUrl || undefined}
      allowManageRecording={(profile?.roles || []).some((role) =>
        [AvailableRoles.HIRING_ADMIN, AvailableRoles.INSIDE_REVIEWER].includes(
          role.value as AvailableRoles
        )
      )}
    />
  );
}
